/**
 * Light-mode background variants — the light counterpart to
 * lib/dark-mode-variants.ts, derived from the FINAL DIRECTION palette
 * (ivory #F5F3EE page surface, white cards, warm linen tints, cobalt-50
 * mist, stone grays). Dark mode is never affected; brand --zom-* neutrals
 * (navbar/chrome) never change. CSS lives in globals.css under
 * `[data-light-variant="..."]:not(.dark)`.
 *
 * Selection defaults to "ivory" — the brand-default pairing, identical to
 * the base light tokens, so first paint never shifts. "none" clears the
 * attribute entirely so custom canvas/surface edits are never fought by a
 * variant block. The document appearance policy imports these definitions for
 * first paint; stored choices belong only to previews.
 */

export const LIGHT_MODE_VARIANT_STORAGE_KEY = "zom-preview-light-variant";

export const LIGHT_MODE_VARIANTS = [
  {
    id: "ivory",
    label: "Ivory",
    description: "Brand default — ivory canvas, white cards, warm hairlines",
    swatches: ["#f5f3ee", "#edebe5", "#ffffff"],
  },
  {
    id: "pure-white",
    label: "Pure White",
    description: "Gallery white canvas with neutral sunken wells",
    swatches: ["#ffffff", "#f4f4f2", "#fafaf9"],
  },
  {
    id: "warm-linen",
    label: "Warm Linen",
    description: "Deeper warm paper canvas from the editorial family",
    swatches: ["#f0ede5", "#e7e3d8", "#fbf9f4"],
  },
  {
    id: "porcelain",
    label: "Porcelain",
    description: "Cobalt-50 mist canvas, white cards — the cool pairing",
    swatches: ["#f1f4fc", "#e6ebf8", "#ffffff"],
  },
  {
    id: "stone",
    label: "Stone",
    description: "Cool stone-gray canvas from the brand stone family",
    swatches: ["#ededee", "#e3e3e5", "#ffffff"],
  },
] as const;

export type LightModeVariant = (typeof LIGHT_MODE_VARIANTS)[number];
export type LightModeVariantId = LightModeVariant["id"];

/**
 * "none" = no `data-light-variant` attribute. The base light tokens (plus
 * any runtime customization) win, so custom canvas/surface edits are never
 * fought by a variant override block.
 */
export const LIGHT_VARIANT_NONE = "none";

export type LightVariantSelection =
  | LightModeVariantId
  | typeof LIGHT_VARIANT_NONE;

export const DEFAULT_LIGHT_VARIANT_SELECTION: LightVariantSelection = "ivory";

export function isLightModeVariantId(
  value: string,
): value is LightModeVariantId {
  return LIGHT_MODE_VARIANTS.some((variant) => variant.id === value);
}

export function isLightVariantSelection(
  value: string,
): value is LightVariantSelection {
  return value === LIGHT_VARIANT_NONE || isLightModeVariantId(value);
}

export function applyLightModeVariant(selection: LightVariantSelection): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (selection === LIGHT_VARIANT_NONE) {
    delete root.dataset.lightVariant;
  } else {
    root.dataset.lightVariant = selection;
  }
}
